<template>
  <div>
    <component :is="component" v-if="!showError"/>
    <ErrorCarregar
      v-if="showError"
      :titulo="errorTitle"
      :texto="errorMessage"
      :show="showError"
      :imagem-url="svgNome"
    />
  </div>
</template>
<script>
import NotFound from '@/views/NotFound/NotFound.vue'
import ErrorCarregar from '@/components/Error/ErrorCarregar.vue'
import { axiosAPI, ConfiguradorResource } from '@/API'
import { mapGetters, mapMutations, mapState } from 'vuex'
import mshubPayload from '@/utils/mshubPayload'
import FuncDefault from '@/views/FuncDefault/FuncDefault'
import FuncionalidadeCustomizada from '@/views/FuncionalidadeCustomizada/FuncionalidadeCustomizada'
import {
  listaDeFuncionalidadesInternas,
  listaDeBaseRotasMSHub,
  rotasDaAplicacao
} from './HandlerEnums'
import { emmitTrocarPagina } from '@/utils/comandosSdk'

export default {
  name: 'Handler',
  components: { ErrorCarregar, NotFound },
  data () {
    return {
      component: null,
      path: '',
      loading: false,
      loadingMessage: '',
      setupFuncionalidade: null,
      showError: false,
      errorMessage: '',
      errorTitle: '',
      svgNome: 'ErrorCloudSvg'
    }
  },
  mounted () {
    this.init(this.$route.path)
  },
  watch: {
    $route (to, from) {
      this.init(to.path)
    }
  },
  computed: {
    ...mapState('SetupApp', ['funcionalidade', 'frameId', 'embedJson']),
    ...mapGetters('SetupApp', ['bundle', 'aplicacao', 'configuracaoAcesso', 'instanciaAplicacao', 'isWidget']),
    ...mapGetters('Login', ['token', 'user']),
    paths () {
      return String.prototype.split
        .call(this.path, ['/'])
        .filter((p) => p !== '')
    },
    idFuncionalidade () {
      if (this.paths[0] === 'playground') {
        return this.paths[1]
      }

      return this.paths[0]
    }
  },
  methods: {
    ...mapMutations('SetupApp', [
      'SET_FUNCIONALIDADE',
      'SET_ID_FUNCIONALIDADE',
      'SET_API_ACESSO',
      'SET_API_FUNCIONALIDADE',
      'SET_API_CMS',
      'SET_API_APLICACAO'
    ]),
    async init (path) {
      this.path = path

      if (this.path === '/404') {
        this.component = NotFound
        return
      }
      if (process.env.NODE_ENV !== 'development') {
        let bundleWeb = window.location.hostname
        const originsIframe = window.location.ancestorOrigins

        if (originsIframe.length > 0) {
          bundleWeb = originsIframe[originsIframe.length - 1]
        }
        const idFuncionalidade = this.idFuncionalidade
        const bundle = this.bundle

        const data = await ConfiguradorResource.validaOrigemFuncionalidade({ bundle, bundleWeb, idFuncionalidade })

        if (!data) {
          this.component = NotFound
          return
        }
      }
      try {
        await this.loadFuncionalidadeSetup()
      } catch (e) {
        this.component = ErrorCarregar
        return
      }

      await this.loadComponent()
    },
    async loadFuncionalidadeSetup () {
      this.configuradorRotasAcesso()
      this.configureRotasAplicacao()
      if (!this.idFuncionalidade) {
        return
      }
      if (
        listaDeFuncionalidadesInternas.indexOf(`${this.idFuncionalidade}`) >= 0
      ) {
        const funcAcessoKey = Object.keys(this.configuracaoAcesso).find(
          (key) => {
            return (
              this.configuracaoAcesso[key].id === `${this.idFuncionalidade}`
            )
          }
        )

        if (funcAcessoKey) {
          this.SET_ID_FUNCIONALIDADE(this.idFuncionalidade)
          this.comandoTrocaDePagina(
            this.configuracaoAcesso[funcAcessoKey].parametros.textos
              .tituloNavegacao
          )
        }
        return // Caso seja do tipo configuracaoAcesso, não precisa buscar os dados
      }
      if (this.isWidget) {
        this.setupFuncionalidade = await ConfiguradorResource.getSetupFuncionalidadeWidget({
          bundle: this.bundle,
          idFuncionalidade: this.idFuncionalidade
        })
        if (this.setupFuncionalidade.hasOwnProperty('status') && !this.setupFuncionalidade.status) {
          this.errorMessage = this.setupFuncionalidade.message
          this.errorTitle = 'Erro no carregamento'
          this.component = null
          this.showError = true
          return
        }
      } else {
        this.setupFuncionalidade = await ConfiguradorResource.getSetupFuncionalidade({
          bundle: this.bundle,
          idFuncionalidade: this.idFuncionalidade
        })
      }

      if (this.setupFuncionalidade) {
        this.SET_FUNCIONALIDADE(this.setupFuncionalidade)
        this.configuradorRotasFuncionalidade()
        this.comandoTrocaDePagina(this.setupFuncionalidade.nome)
      }
    },
    configureRotasAplicacao () {
      Object.keys(rotasDaAplicacao).forEach((apiNome) => {
        const rotas = this.configuradorRotas(
          this.aplicacao.rotas,
          rotasDaAplicacao[apiNome]
        )

        this.SET_API_APLICACAO({
          apiNome,
          rotas
        })
      })
    },
    configuradorRotasFuncionalidade () {
      const apiRotas = this.configuradorRotas(
        this.aplicacao.rotas,
        this.funcionalidade.rotas
      )

      this.SET_API_FUNCIONALIDADE(apiRotas)
    },
    configuradorRotasAcesso () {
      let apiRotasAcesso = {}

      let acesso = this.configuracaoAcesso
      Object.keys(acesso).forEach((k) => {
        if (!acesso[k].ativo || !acesso[k].rotas) {
          return
        }
        apiRotasAcesso[k] = this.configuradorRotas(
          this.aplicacao.rotas,
          acesso[k].rotas
        )
      })

      this.SET_API_ACESSO(apiRotasAcesso)
    },
    configuradorRotas (rotasBase, rotas) {
      const apiRotas = {}

      if (!rotas) {
        return apiRotas
      }
      Object.keys(rotas).forEach((k) => {
        if (rotasBase[rotas[k].urlBase]) {
          apiRotas[k] = async (opt) => {
            opt = opt || {}
            let payload = opt.payload || {}
            const customHeaders = opt.headers || {}
            const queryParams = opt.query || {}
            const params = opt.params
            const loading = opt.loading
            const notifyFailure = opt.notifyFailure
            const notifySuccess = opt.notifySuccess
            const handlerErrorIgnore = opt.handlerErrorIgnore
            const options = opt.options || {}

            let loader
            if (loading) {
              loader = this.$loading({
                lock: true,
                fullscreen: true,
                text: typeof loading === 'string' ? loading : '',
                customClass: 'myloading'
              })
            }

            const method = rotas[k].metodo.toLowerCase()
            let url = rotasBase[rotas[k].urlBase].rota + rotas[k].path

            if (params) {
              Object.keys(params).forEach((key) => {
                url = url.replace(`:${key}`, params[key])
              })
            }

            const headers = {
              'content-type': rotas[k].contentType,
              instancia_aplicacao: this.instanciaAplicacao,
              ...customHeaders
            }

            let data

            if (listaDeBaseRotasMSHub.indexOf(rotas[k].urlBase) !== -1) {
              payload = mshubPayload(payload)
              headers.mshash = this.aplicacao.mshash
            }

            headers.bundle = this.bundle

            switch (rotas[k].contentType) {
              case 'multipart/form-data':
                const formData = new FormData()

                Object.keys(payload).forEach((key) => {
                  formData.append(key, payload[key])
                })

                data = formData
                break

              case 'application/json':
              default:
                data = {
                  ...payload
                }
            }

            const request = axiosAPI({
              method,
              url,
              data,
              headers,
              params: queryParams
            })

            const optionsData = {
              ...options,
              notifyFailure,
              notifySuccess,
              handlerErrorIgnore
            }

            const result = await axiosAPI.notifyRequest(request, optionsData)

            if (loading) {
              setTimeout(() => {
                loader.close()
              }, 300)
            }

            return result
          }
        }
      })

      return apiRotas
    },
    async loadComponent () {
      if (this.paths && this.paths.length) {
        let index = this.path.startsWith('/playground') ? 2 : 1
        const idFunc = parseInt(this.path.split('/')[index])

        if (idFunc >= 9000 && idFunc <= 20000) {
          if (this.paths.length > 1) {
            try {
              let componentName = 'FuncionalidadeCustomizada'
              componentName += this.capitalizeFirstLetter(this.paths[index])

              const component = require(`../FuncionalidadeCustomizada/${componentName}.vue`)

              if (component) {
                this.component = component.default
                return
              }
            } catch (ignored) {}
          }

          this.component = FuncionalidadeCustomizada
          return
        }

        let componentName = `Func`

        for (let i = 0; i < this.paths.length; i++) {
          if (this.paths[i] === 'playground') {
            continue
          }
          componentName += this.capitalizeFirstLetter(this.paths[i])
        }

        try {
          const component = require(`../${this.idFuncionalidade}/${componentName}.vue`)

          if (component) {
            this.component = component.default
            return
          }
        } catch (ignored) {}

        this.component = FuncDefault
        return
      }

      this.component = NotFound
    },
    capitalizeFirstLetter (string) {
      return (
        String.prototype.charAt.call(string, 0).toUpperCase() +
        String.prototype.slice.call(string, 1)
      )
    },
    comandoTrocaDePagina (titulo) {
      emmitTrocarPagina(this.frameId, { titulo: titulo, idFuncionalidade: this.idFuncionalidade })
    }
  }
}
</script>

<style>
.myloading {
  display: flex;
  align-content: center;
  justify-content: center;
}
.myloading > .el-loading-spinner {
  width: fit-content;
  padding: 30px 50px;
  min-width: 150px;
  background: #ffffff 0 0 no-repeat padding-box;
  box-shadow: 0 2px 10px #0000000d;
  border-radius: 20px;
}
</style>
